import type {
  Country,
  EnumDefinition,
  EnumItemBase,
  EnumValues,
} from "@dfp/isomorphic-enums";

import { projectName } from "../projectName.ts";
import type { Bank } from "./bank.ts";
import type { PaymentProviderAggregator } from "./paymentProviderAggregator.ts";

export interface PaymentProviderEnumItem extends EnumItemBase {
  name: string;
  bank: Bank;
  provider: PaymentProviderAggregator;
  isPopular: boolean;
  isMock: boolean;
  countryCode: Country;
}

export type PaymentProvider = EnumValues<typeof paymentProviderEnum>;

export const paymentProviderEnum = {
  header: {
    businessName: "Payment Provider",
    businessOwner: "Core team",
    dataSource: "Internal",
    description: "Supported payment providers",
    scopeOfUsage: "Shared",
    technicalName: "paymentProvider",
    projectName,
    additionalAttributes: {
      name: {
        dataSource: "Internal",
        description: "Name of payment provider.",
      },
      bank: {
        dataSource: "Internal",
        description: "Bank of payment provider.",
      },
      provider: {
        dataSource: "Internal",
        description: "Finbricks | Salt Edge | FxMockBankaggregatator",
      },
      isPopular: {
        dataSource: "Internal",
        description: "Priority for display.",
      },
      isMock: {
        dataSource: "Internal",
        description:
          "Whether the payment provider is a mock provider for testing purposes.",
      },
      countryCode: { dataSource: "Internal", description: "" },
    },
  },
  values: [
    /* ---------------------------------
     * CZE
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "fio_oauth_client_cz",
      name: "Fio banka SaltEdge",
      bank: "FIOBCZPPXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "raiffeisen_oauth_client_cz",
      name: "Raiffeisen Bank SaltEdge",
      bank: "RZBCCZPPXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "csob_oauth_client_cz",
      name: "Československá obchodní banka SaltEdge",
      bank: "CEKOCZPPXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "MONETA",
      name: "Moneta Money Bank",
      bank: "AGBACZPPXXX",
      provider: "Finbricks",
      isPopular: true,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "FIO",
      name: "Fio banka",
      bank: "FIOBCZPPXXX",
      provider: "Finbricks",
      isPopular: false,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "CREDITAS",
      name: "Banka CREDITAS",
      bank: "CTASCZ22XXX",
      provider: "Finbricks",
      isPopular: false,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "creditas_oauth_client_cz",
      name: "Banka CREDITAS SaltEdge",
      bank: "CTASCZ22XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "MBANK",
      name: "mBank",
      bank: "BREXCZPPXXX",
      provider: "Finbricks",
      isPopular: false,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "mbank_oauth_client_cz",
      name: "mBank SaltEdge",
      bank: "BREXCZPPXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "RAIFFEISEN",
      name: "Raiffeisen Bank",
      bank: "RZBCCZPPXXX",
      provider: "Finbricks",
      isPopular: true,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "UNICREDIT",
      name: "UniCredit Bank",
      bank: "BACXCZPPXXX",
      provider: "Finbricks",
      isPopular: true,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "unicredit_client_cz",
      name: "UniCredit Bank SaltEdge",
      bank: "BACXCZPPXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "CSAS",
      name: "Česká spořitelna",
      bank: "GIBACZPXXXX",
      provider: "Finbricks",
      isPopular: true,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "JTB",
      name: "J&T Banka",
      bank: "JTBPCZPPXXX",
      provider: "Finbricks",
      isPopular: false,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "AIRBANK",
      name: "Air Bank",
      bank: "AIRACZPPXXX",
      provider: "Finbricks",
      isPopular: false,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "airbank_oauth_client_cz",
      name: "Air bank SaltEdge",
      bank: "AIRACZPPXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "CSOB",
      name: "Československá obchodní banka",
      bank: "CEKOCZPPXXX",
      provider: "Finbricks",
      isPopular: true,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "KB",
      name: "Komerční banka",
      bank: "KOMBCZPPXXX",
      provider: "Finbricks",
      isPopular: true,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "kb_oauth_client_cz",
      name: "Komerční banka SaltEdge",
      bank: "KOMBCZPPXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCK_COBS",
      name: "Mock Bank",
      bank: "MOCKBANKXXX",
      provider: "Finbricks",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCK_COBS_2",
      name: "Mock Bank 2",
      bank: "MOCKBANK2XX",
      provider: "Finbricks",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCK_ALLATRA_BANK",
      name: "Allatra Bank",
      bank: "MOCKALABXXX",
      provider: "FxMockBankaggregator",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCK_CITRIX_BANK",
      name: "Citrix Bank",
      bank: "MOCKCITBXXX",
      provider: "FxMockBankaggregator",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCK_BUNBONAMBO_BANK",
      name: "Bunbonambo Bank",
      bank: "MOCKBUNBXXX",
      provider: "FxMockBankaggregator",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCK_SEPULTURA_BANK",
      name: "Sepultura Bank",
      bank: "MOCKSEPBXXX",
      provider: "FxMockBankaggregator",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "moneta_oauth_client_cz",
      name: "Moneta Money Bank SaltEdge",
      bank: "AGBACZPPXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "csas_oauth_client_cz",
      name: "Česká spořitelna SaltEdge",
      bank: "GIBACZPXXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_cz",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "hsbcnet_oauth_client_cz",
      name: "HSBCnet",
      bank: "MIDLCZPPXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "ing_wb_oauth_client_cz",
      name: "ING Wholesale Banking (InsideBusiness)",
      bank: "INGBCZPPXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "oberbank_client_cz",
      name: "Oberbank",
      bank: "OBKLCZ2XXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bnpparibas_oauth_client_cz",
      name: "Connexis Cash (BNP Paribas)",
      bank: "GEBACZPPXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "paypal_oauth_client_cz",
      name: "PayPal",
      bank: "PPLXLUL2",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    /* ---------------------------------
     * Salt Edge generic sandbox fake providers (testing only).
     * Require include_sandboxes: true on the /connect call.
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "fake_client_xf",
      name: "Fake Bank (Salt Edge)",
      bank: "MOCKFAKEXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "fakebank_simple_xf",
      name: "Fake Bank Simple (Salt Edge)",
      bank: "MOCKFSIMXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "fake_oauth_client_xf",
      name: "Fake Bank OAuth (Salt Edge)",
      bank: "MOCKOAUTXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "CZE",
    },
    /* ---------------------------------
     * SVK
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_sk",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "mbank_oauth_client_sk",
      name: "mBank",
      bank: "BREXSKBX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "slovenska_sporitelna_oauth_client_sk",
      name: "Slovenská sporiteľňa",
      bank: "GIBASKBX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "csob_oauth_client_sk",
      name: "ČSOB",
      bank: "CEKOSKBX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "raiffeisen_oauth_client_sk",
      name: "Raiffeisen",
      bank: "TATRSKBX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "tatra_banka_oauth_client_sk",
      name: "Tatra Banka",
      bank: "TATRSKBX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "unicredit_client_sk",
      name: "UniCredit Bank",
      bank: "UNCRSKBX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "primabanka_oauth_client_sk",
      name: "Prima banka",
      bank: "KOMASK2X",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "transferwise_oauth_client_sk",
      name: "Wise",
      bank: "TRWIBEBB",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "fio_oauth_client_sk",
      name: "Fio banka",
      bank: "FIOZSKBA",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "kb_oauth_client_sk",
      name: "Komerční banka",
      bank: "KOMBSKBA",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "vub_banka_oauth_client_sk",
      name: "VUB",
      bank: "SUBASKBX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bank_365_client_sk",
      name: "365 Bank",
      bank: "POBNSKBA",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SVK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "paypal_oauth_client_sk",
      name: "PayPal",
      bank: "PPLXLUL2",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "SVK",
    },
    /* ---------------------------------
     * POL
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_pl",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "mbank_oauth_client_pl",
      name: "mBank",
      bank: "BREXPLPW",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "mbank_corporate_oauth_client_pl",
      name: "mBank Corporate",
      bank: "BREXPLPW",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "alior_bank_oauth_client_pl",
      name: "Alior Bank",
      bank: "ALBPPLPW",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bank_pekao_oauth_client_pl",
      name: "Bank Pekao",
      bank: "PKOPPLPW",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bnp_paribas_oauth_client_pl",
      name: "BNP Paribas",
      bank: "PPABPLPK",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bnp_paribas_business_oauth_client_pl",
      name: "BNP Paribas Business",
      bank: "PPABPLPK",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "pkobp_oauth_client_pl",
      name: "PKO Bank Polski",
      bank: "BPKOPLPW",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "pkobp_corporate_oauth_client_pl",
      name: "PKO Bank Polski Corporate",
      bank: "BPKOPLPW",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "citi_oauth_client_pl",
      name: "Citi Handlowy",
      bank: "CITIPLPX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "millenium_oauth_client_pl",
      name: "Millennium Bank",
      bank: "BIGBPLPW",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "santander_oauth_client_pl",
      name: "Erste Bank Polska",
      bank: "WBKPPLPP",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "ing_oauth_client_pl",
      name: "ING",
      bank: "INGBPLPW",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "POL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "paypal_oauth_client_pl",
      name: "PayPal",
      bank: "PPLXLUL2",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "POL",
    },
    /* ---------------------------------
     * DEU
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "deutsche_bank_client_de",
      name: "Deutsche Bank",
      bank: "DEUTDEFF",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "DEU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "commerzbank_oauth_client_de",
      name: "Commerzbank",
      bank: "COBADEFF",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "DEU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "frankfurter_volksbank_client_de",
      name: "Frankfurter Volksbank RHEIN-MAIN eG",
      bank: "FFVBDEFF",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "DEU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "unicredit_oauth_client_de",
      name: "Hypovereinsbank",
      bank: "HYVEDEMM",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "DEU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "postbank_client_de",
      name: "Postbank",
      bank: "PBNKDEFF",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "DEU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "ing_oauth_client_de",
      name: "ING",
      bank: "INGDDEFF",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "DEU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "lbbw_oauth_client_de",
      name: "LBBW",
      bank: "SOLADEST",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "DEU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_de",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "DEU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "qonto_bank_oauth_client_de",
      name: "Qonto",
      bank: "QNTODEB2",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "DEU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "paypal_oauth_client_de",
      name: "PayPal",
      bank: "PPLXLUL2",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "DEU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "spk_oberlausitz_niederschlesien_oauth_client_de",
      name: "SPK Oberlausitz-Niederschlesien",
      bank: "WELADED1GRL",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "DEU",
    },
    /* ---------------------------------
     * AUT
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "btv_client_at",
      name: "BTV Vier Länder Bank AG",
      bank: "BTVAAT22XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "oberbank_client_at",
      name: "Oberbank",
      bank: "OBKLAT2LXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bks_client_at",
      name: "BKS Bank",
      bank: "BFKKAT2KXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "addiko_bank_oauth_client_at",
      name: "Addiko Bank",
      bank: "HSEEAT2KXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "anadi_bank_oauth_client_at",
      name: "Austrian Anadi Bank",
      bank: "HAABAT2KXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bawag_psk_oauth_client_at",
      name: "BAWAG P.S.K.",
      bank: "BAWAATWWXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "easybank_oauth_client_at",
      name: "Easybank",
      bank: "EASYATW1XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bank_99_oauth_client_at",
      name: "Bank 99",
      bank: "SPBAATWWXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "erste_bank_sparkassen_oauth_client_at",
      name: "Erste Bank and Sparkassen",
      bank: "GIBAATWWXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "generali_bank_oauth_client_at",
      name: "Generali Bank",
      bank: "BGENATWWXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "hypo_noe_oauth_client_at",
      name: "Hypo Noe",
      bank: "HYPNATWWXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "hypo_tirol_oauth_client_at",
      name: "Hypo Tirol",
      bank: "HYPTAT22XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "hypo_vorarlberg_oauth_client_at",
      name: "Hypo Vorarlberg",
      bank: "HYPVAT2BXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "raiffeisen_client_at",
      name: "Raiffeisen",
      bank: "RZBAATWWXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_at",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "unicredit_business_oauth_client_at",
      name: "Bank Austria Unicredit BusinessNet",
      bank: "BKAUATWWXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "unicredit_oauth_client_at",
      name: "Bank Austria Unicredit",
      bank: "BKAUATWWXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "vr_bank_rottal_inn_eg_oauth_client_at",
      name: "VR-Bank Rottal-Inn eG",
      bank: "VRBKAT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "volksbank_niederosterreich_oauth_client_at",
      name: "Volksbank Niederösterreich",
      bank: "VBOEATWWNOM",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "volksbank_oberosterreich_oauth_client_at",
      name: "Volksbank Oberösterreich",
      bank: "VBOEATWWOOE",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "volksbank_salzburg_oauth_client_at",
      name: "Volksbank Salzburg",
      bank: "VBOEATWWSAL",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "volksbank_steiermark_oauth_client_at",
      name: "Volksbank Steiermark",
      bank: "VBOEATWWGRA",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "volksbank_tirol_oauth_client_at",
      name: "Volksbank Tirol",
      bank: "VBOEATWWINN",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "volksbank_vorarlberg_oauth_client_at",
      name: "Volksbank Vorarlberg",
      bank: "VOVBAT2BXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "volksbank_kaernten_oauth_client_at",
      name: "Volksbank Kaernten",
      bank: "VBOEATWWKLA",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "transferwise_oauth_client_at",
      name: "Wise",
      bank: "TRWIBEBB",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "volksbank_wien_oauth_client_at",
      name: "Volksbank Wien",
      bank: "VBOEATWWXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "AUT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "paypal_oauth_client_at",
      name: "PayPal",
      bank: "PPLXLUL2",
      provider: "SaltEdge",
      isPopular: false,
      isMock: true,
      countryCode: "AUT",
    },
    /* ---------------------------------
     * BEL
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "bnpparibas_fortis_oauth_client_be",
      name: "BNP Paribas Fortis",
      bank: "GEBABEBBXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "BEL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "kbc_oauth_client_be",
      name: "KBC",
      bank: "KREDBEBBXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "BEL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "belfius_client_be",
      name: "Belfius",
      bank: "GKCCBEBBXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "BEL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "ing_oauth_client_be",
      name: "ING",
      bank: "BBRUBEBBXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "BEL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "argenta_oauth_client_be",
      name: "Argenta",
      bank: "ARSPBE22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "BEL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "crelan_oauth_client_be",
      name: "Crelan",
      bank: "NICABEBBXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "BEL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "triodos_oauth_client_be",
      name: "Triodos",
      bank: "TRIOBEBBTG2",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "BEL",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_be",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "BEL",
    },

    /* ---------------------------------
     * BGR
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "unicredit_oauth_client_bg",
      name: "UniCredit Bulbank",
      bank: "UNCRBGSFXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "BGR",
    },
    {
      valueUsage: "CanBeEntered",
      value: "dsk_oauth_client_bg",
      name: "DSK Bank",
      bank: "STSABGSFXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "BGR",
    },
    {
      valueUsage: "CanBeEntered",
      value: "ubb_oauth_client_bg",
      name: "UBB",
      bank: "UBBSBGSFXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "BGR",
    },
    {
      valueUsage: "CanBeEntered",
      value: "postbank_oauth_client_bg",
      name: "Пощенска Банка",
      bank: "BPBIBGSFXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "BGR",
    },
    {
      valueUsage: "CanBeEntered",
      value: "fibank_client_bg",
      name: "Fibank",
      bank: "FINVBGSFXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "BGR",
    },
    {
      valueUsage: "CanBeEntered",
      value: "ccb_oauth_client_bg",
      name: "Centralna Kooperativna Banka",
      bank: "CECBBGSFXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "BGR",
    },
    // TODO CAS-1784: no Salt Edge provider matched "Allianz Bank Bulgaria"
    {
      valueUsage: "CanBeEntered",
      value: "procreditbank_client_bg",
      name: "ProCredit Bank",
      bank: "PRCBBGSFXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "BGR",
    },
    {
      valueUsage: "CanBeEntered",
      value: "tbibank_client_bg",
      name: "TBIBank",
      bank: "TBIBBGSFXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "BGR",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_bg",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "BGR",
    },

    /* ---------------------------------
     * HRV
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "unicredit_oauth_client_hr",
      name: "Zagrebacka banka d.d.",
      bank: "ZABAHR2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "HRV",
    },
    {
      valueUsage: "CanBeEntered",
      value: "pbz_client_hr",
      name: "Privredna Banka Zagreb",
      bank: "PBZGHR2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "HRV",
    },
    {
      valueUsage: "CanBeEntered",
      value: "erste_steiermarkische_bank_oauth_client_hr",
      name: "Erste&Steiermärkische Bank d.d.",
      bank: "ESBCHR22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "HRV",
    },
    {
      valueUsage: "CanBeEntered",
      value: "otp_bank_oauth_client_hr",
      name: "OTP banka",
      bank: "OTPBHR2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "HRV",
    },
    {
      valueUsage: "CanBeEntered",
      value: "raiffeisen_oauth_client_hr",
      name: "Raiffeisen",
      bank: "RZBHHR2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "HRV",
    },
    {
      valueUsage: "CanBeEntered",
      value: "hpb_client_hr",
      name: "HPB",
      bank: "HPBZHR2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "HRV",
    },
    // TODO CAS-1784: no Salt Edge provider matched "Addiko Bank"
    {
      valueUsage: "CanBeEntered",
      value: "agram_banka_client_hr",
      name: "Agram Banka",
      bank: "KREZHR2XXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "HRV",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_hr",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "HRV",
    },
    // TODO CAS-1784: no Salt Edge provider matched "KentBank"

    /* ---------------------------------
     * DNK
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "danske_bank_oauth_client_dk",
      name: "Danske Bank",
      bank: "DABADKKKXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "DNK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "nordea_oauth_client_dk",
      name: "Nordea",
      bank: "NDEADKKKXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "DNK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "lan_spar_bank_oauth_client_dk",
      name: "Lån & Spar Bank",
      bank: "LOSADKKKXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "DNK",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_dk",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "DNK",
    },

    /* ---------------------------------
     * EST
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "swedbank_oauth_client_ee",
      name: "Swedbank",
      bank: "HABAEE2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "EST",
    },
    {
      valueUsage: "CanBeEntered",
      value: "seb_oauth_client_ee",
      name: "SEB",
      bank: "EEUHEE2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "EST",
    },
    {
      valueUsage: "CanBeEntered",
      value: "lhv_oauth_client_ee",
      name: "LHV",
      bank: "LHVBEE22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "EST",
    },
    {
      valueUsage: "CanBeEntered",
      value: "luminor_oauth_client_ee",
      name: "Luminor",
      bank: "RIKOEE22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "EST",
    },
    {
      valueUsage: "CanBeEntered",
      value: "cooppank_oauth_client_ee",
      name: "Coop Pank",
      bank: "EKRDEE22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "EST",
    },
    // TODO CAS-1784: no Salt Edge provider matched "Bigbank"
    {
      valueUsage: "CanBeEntered",
      value: "citadele_client_ee",
      name: "Citadele Bank",
      bank: "PARXEE22XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "EST",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_ee",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "EST",
    },
    {
      valueUsage: "CanBeEntered",
      value: "transferwise_oauth_client_ee",
      name: "Wise (former Transferwise)",
      bank: "TRWIBEBB",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "EST",
    },
    // TODO CAS-1784: no Salt Edge provider matched "TBB pank"

    /* ---------------------------------
     * FIN
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "op_oauth_client_fi",
      name: "OP",
      bank: "OKOYFIHHXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "FIN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "nordea_oauth_client_fi",
      name: "Nordea",
      bank: "NDEAFIHHXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "FIN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "danske_bank_oauth_client_fi",
      name: "Danske Bank",
      bank: "DABAFIHHXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "FIN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "spankki_oauth_client_fi",
      name: "S-Pankki",
      bank: "SBANFIHHXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "FIN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "saving_bank_oauth_client_fi",
      name: "Säästöpankki",
      bank: "ITELFIHHXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "FIN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "oma_savings_bank_oauth_client_fi",
      name: "Oma Säästöpankki",
      bank: "OMSAFI2SXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "FIN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "aktia_client_fi",
      name: "Aktia",
      bank: "HELSFIHHXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "FIN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "pop_pankki_oauth_client_fi",
      name: "POP Pankki",
      bank: "POPFFI22XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "FIN",
    },
    // TODO CAS-1784: no Salt Edge provider matched "Holvi"
    {
      valueUsage: "CanBeEntered",
      value: "holvi_client_de",
      name: "Holvi",
      bank: "HOLVDEB1XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "FIN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "alandsbanken_oauth_client_fi",
      name: "Ålandsbanken",
      bank: "AABAFI22XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "FIN",
    },

    /* ---------------------------------
     * FRA
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "bnpparibas_oauth_client_fr",
      name: "Connexis Cash (BNP Paribas)",
      bank: "BNPAFRPPXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "FRA",
    },
    {
      valueUsage: "CanBeEntered",
      value: "societe_generale_oauth_client_fr",
      name: "Société Générale (Particuliers)",
      bank: "SOGTFRP1XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "FRA",
    },
    {
      valueUsage: "CanBeEntered",
      value: "cic_oauth_client_fr",
      name: "CIC",
      bank: "CMCIFRCPCIC",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "FRA",
    },
    {
      valueUsage: "CanBeEntered",
      value: "lcl_oauth_client_fr",
      name: "LCL",
      bank: "CRLYFRPPXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "FRA",
    },
    {
      valueUsage: "CanBeEntered",
      value: "qonto_bank_oauth_client_fr",
      name: "Qonto",
      bank: "QNTOFRP1XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "FRA",
    },

    /* ---------------------------------
     * GRC
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "national_bank_of_greece_oauth_client_gr",
      name: "National Bank of Greece",
      bank: "ETHNGRAAXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "GRC",
    },
    {
      valueUsage: "CanBeEntered",
      value: "piraeus_bank_oauth_client_gr",
      name: "Piraeus Bank",
      bank: "PIRBGRAAXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "GRC",
    },
    {
      valueUsage: "CanBeEntered",
      value: "eurobank_oauth_client_gr",
      name: "Eurobank",
      bank: "ERBKGRAAXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "GRC",
    },
    {
      valueUsage: "CanBeEntered",
      value: "alpha_bank_oauth_client_gr",
      name: "Alpha Bank",
      bank: "CRBAGRAAXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "GRC",
    },
    // TODO CAS-1784: no Salt Edge provider matched "Optima Bank"
    // TODO CAS-1784: no Salt Edge provider matched "Attica Bank"
    // TODO CAS-1784: no Salt Edge provider matched "Viva.com"
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_gr",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "GRC",
    },
    // TODO CAS-1784: no Salt Edge provider matched "Pancreta Bank"
    // TODO CAS-1784: no Salt Edge provider matched "Cooperative banks (Epirus, Karditsa)"

    /* ---------------------------------
     * HUN
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "otpbank_client_hu",
      name: "OTP Bank",
      bank: "OTPVHUHBXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "HUN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "kh_oauth_client_hu",
      name: "K&H",
      bank: "OKHBHUHBXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "HUN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "erste_bank_oauth_client_hu",
      name: "Erste Bank",
      bank: "GIBAHUHBXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "HUN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "unicredit_oauth_client_hu",
      name: "UniCredit Bank Hungary Zrt",
      bank: "BACXHUHBXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "HUN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "raiffeisen_oauth_client_hu",
      name: "Raiffeisen",
      bank: "UBRTHUHBXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "HUN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "cib_oauth_client_hu",
      name: "CIB",
      bank: "CIBHHUHBXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "HUN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "granit_bank_oauth_client_hu",
      name: "GRÁNIT Bank ZRT.",
      bank: "GNBAHUHBXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "HUN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_hu",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "HUN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "magnetbank_oauth_client_hu",
      name: "MagNet Bank",
      bank: "HBWEHUHBXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "HUN",
    },

    /* ---------------------------------
     * ITA
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "intesa_sanpaolo_corporate_oauth_client_it",
      name: "Intesa Sanpaolo InBiz",
      bank: "BCITITMMXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ITA",
    },
    {
      valueUsage: "CanBeEntered",
      value: "unicredit_oauth_client_it",
      name: "UniCredit S.p.A.",
      bank: "UNCRITMMXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ITA",
    },
    {
      valueUsage: "CanBeEntered",
      value: "banco_bpm_oauth_client_it",
      name: "Banco BPM Privati (YouWeb)",
      bank: "BAPPIT22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ITA",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bper_banca_oauth_client_it",
      name: "BPER Banca Privati",
      bank: "BPMOIT22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ITA",
    },
    {
      valueUsage: "CanBeEntered",
      value: "banco_posta_oauth_client_it",
      name: "Poste Italiane S.p.A.- Patrimonio BancoPosta",
      bank: "BPPIITRRXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ITA",
    },

    /* ---------------------------------
     * LVA
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "swedbank_oauth_client_lv",
      name: "Swedbank",
      bank: "HABALV22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "LVA",
    },
    {
      valueUsage: "CanBeEntered",
      value: "seb_oauth_client_lv",
      name: "SEB",
      bank: "UNLALV2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "LVA",
    },
    {
      valueUsage: "CanBeEntered",
      value: "citadele_client_lv",
      name: "Citadele Bank",
      bank: "PARXLV22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "LVA",
    },
    {
      valueUsage: "CanBeEntered",
      value: "luminor_oauth_client_lv",
      name: "Luminor",
      bank: "RIKOLV2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "LVA",
    },
    // TODO CAS-1784: no Salt Edge provider matched "Rietumu Banka"
    // TODO CAS-1784: no Salt Edge provider matched "BluOr Bank"
    // TODO CAS-1784: no Salt Edge provider matched "Industra Bank"
    // TODO CAS-1784: no Salt Edge provider matched "Signet Bank"
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_lv",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "LVA",
    },
    {
      valueUsage: "CanBeEntered",
      value: "transferwise_oauth_client_lv",
      name: "Wise (former Transferwise)",
      bank: "TRWIBEBB",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "LVA",
    },

    /* ---------------------------------
     * LTU
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "swedbank_oauth_client_lt",
      name: "Swedbank",
      bank: "HABALT22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "LTU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "seb_oauth_client_lt",
      name: "SEB",
      bank: "CBVILT2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "LTU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "siauliu_bankas_client_lt",
      name: "Artea",
      bank: "CBSBLT26XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "LTU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "luminor_oauth_client_lt",
      name: "Luminor",
      bank: "AGBLLT2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "LTU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_lt",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "LTU",
    },
    // TODO CAS-1784: no Salt Edge provider matched "Paysera LT"
    {
      valueUsage: "CanBeEntered",
      value: "citadele_client_lt",
      name: "Citadele Bank",
      bank: "INDULT2XXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "LTU",
    },
    // TODO CAS-1784: no Salt Edge provider matched "Urbo bankas"
    // TODO CAS-1784: no Salt Edge provider matched "Mano bankas"
    {
      valueUsage: "CanBeEntered",
      value: "transferwise_oauth_client_lt",
      name: "Wise (former Transferwise)",
      bank: "TRWIBEBB",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "LTU",
    },

    /* ---------------------------------
     * NLD
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "ing_oauth_client_nl",
      name: "ING",
      bank: "INGBNL2AXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "NLD",
    },
    {
      valueUsage: "CanBeEntered",
      value: "rabobank_oauth_client_nl",
      name: "Rabobank",
      bank: "RABONL2UXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "NLD",
    },
    {
      valueUsage: "CanBeEntered",
      value: "abn_amro_oauth_client_nl",
      name: "ABN AMRO",
      bank: "ABNANL2AXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "NLD",
    },
    {
      valueUsage: "CanBeEntered",
      value: "regiobank_oauth_client_nl",
      name: "ASN Bank vh RegioBank",
      bank: "RBRBNL21XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "NLD",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bunq_oauth_client_nl",
      name: "bunq",
      bank: "BUNQNL2AXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "NLD",
    },
    {
      valueUsage: "CanBeEntered",
      value: "knab_client_nl",
      name: "Knab",
      bank: "KNABNL2HXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "NLD",
    },
    {
      valueUsage: "CanBeEntered",
      value: "triodos_oauth_client_nl",
      name: "Triodos",
      bank: "TRIONL2UXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "NLD",
    },
    {
      valueUsage: "CanBeEntered",
      value: "asnbank_oauth_client_nl",
      name: "ASN Bank",
      bank: "ASNBNL21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "NLD",
    },
    {
      valueUsage: "CanBeEntered",
      value: "van_lanschot_kempen_oauth_client_nl",
      name: "Van Lanschot Kempen",
      bank: "FVLBNL22XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "NLD",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_nl",
      name: "Revolut",
      bank: "REVONL22XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "NLD",
    },

    /* ---------------------------------
     * NOR
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "dnb_oauth_client_no",
      name: "DNB",
      bank: "DNBANOKKXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "NOR",
    },
    {
      valueUsage: "CanBeEntered",
      value: "nordea_oauth_client_no",
      name: "Nordea",
      bank: "NDEANOKKXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "NOR",
    },
    // TODO CAS-1784: no Salt Edge provider matched "SpareBank 1 SR-Bank"
    // TODO CAS-1784: no Salt Edge provider matched "SpareBank 1 SMN"
    // TODO CAS-1784: no Salt Edge provider matched "SpareBank 1 Nord-Norge"
    // TODO CAS-1784: no Salt Edge provider matched "SpareBank 1 Østlandet"
    // TODO CAS-1784: no Salt Edge provider matched "Sparebanken Vest"
    {
      valueUsage: "CanBeEntered",
      value: "handelsbanken_client_no",
      name: "Handelsbanken",
      bank: "HANDNOKKXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "NOR",
    },
    {
      valueUsage: "CanBeEntered",
      value: "danske_bank_oauth_client_no",
      name: "Danske Bank",
      bank: "DABANO22XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "NOR",
    },
    {
      valueUsage: "CanBeEntered",
      value: "storebrand_oauth_client_no",
      name: "Storebrand",
      bank: "STFBNOKKXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "NOR",
    },

    /* ---------------------------------
     * PRT
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "cgd_oauth_client_pt",
      name: "Caixa Geral de Depósitos S.A",
      bank: "CGDIPTPLXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "PRT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "banco_comercial_portugues_oauth_client_pt",
      name: "Banco Comercial Português S.A",
      bank: "BCOMPTPLXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "PRT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "novo_banco_oauth_client_pt",
      name: "Novo Banco S.A",
      bank: "BESCPTPLXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "PRT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "santander_totta_oauth_client_pt",
      name: "Banco Santander S.A",
      bank: "TOTAPTPLXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "PRT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bpi_oauth_client_pt",
      name: "Banco Português de Investimento S.A",
      bank: "BBPIPTPLXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "PRT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "credito_agricola_oauth_client_pt",
      name: "Crédito Agrícola (Moey)",
      bank: "CCCMPTPLXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "PRT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "montepio_oauth_client_pt",
      name: "Banco Montepio",
      bank: "MPIOPTPLXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "PRT",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bankinter_oauth_client_pt",
      name: "Bankinter S.A",
      bank: "BKBKPTPLXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "PRT",
    },
    // TODO CAS-1784: no Salt Edge provider matched "Banco CTT"
    {
      valueUsage: "CanBeEntered",
      value: "activobank_oauth_client_pt",
      name: "Banco Activobank S.A",
      bank: "ACTVPTPLXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "PRT",
    },

    /* ---------------------------------
     * ROU
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "bt_trans_oauth_client_ro",
      name: "Banca Transilvania",
      bank: "BTRLRO22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ROU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bcr_client_oauth_ro",
      name: "Banca Comerciala Romana",
      bank: "RNCBROBUXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ROU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "brd_client_ro",
      name: "BRD",
      bank: "BRDEROBUXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ROU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "ing_oauth_client_ro",
      name: "ING",
      bank: "INGBROBUXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ROU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "raiffeisen_oauth_client_ro",
      name: "Raiffeisen",
      bank: "RZBRROBUXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ROU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "unicredit_oauth_client_ro",
      name: "UniCredit Bank",
      bank: "BACXROBUXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ROU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_ro",
      name: "Revolut",
      bank: "REVOROBBXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "ROU",
    },
    {
      valueUsage: "CanBeEntered",
      value: "librabank_oauth_client_ro",
      name: "Libra Internet Bank",
      bank: "BRELROBUXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "ROU",
    },
    // TODO CAS-1784: no Salt Edge provider matched "Salt Bank"

    /* ---------------------------------
     * SVN
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "nlb_oauth_client_si",
      name: "NLB",
      bank: "LJBASI2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SVN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "nkbm_oauth_client_si",
      name: "OTP banka d.d.",
      bank: "KBMASI2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SVN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "intesa_sanpaolo_oauth_client_si",
      name: "Intesa Sanpaolo",
      bank: "BAKOSI2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SVN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "unicredit_oauth_client_si",
      name: "UniCredit Bank",
      bank: "BACXSI22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SVN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "addiko_oauth_client_si",
      name: "Addiko Bank",
      bank: "HAABSI22XXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SVN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "gbkr_oauth_client_si",
      name: "Gorenjska Banka",
      bank: "GORESI2XXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SVN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "delavska_hranilnica_oauth_client_si",
      name: "Delavska Hranilnica",
      bank: "HDELSI22XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SVN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "sparkasse_oauth_client_si",
      name: "Sparkasse",
      bank: "KSPKSI22XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SVN",
    },
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_si",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SVN",
    },

    {
      valueUsage: "CanBeEntered",
      value: "n26_xs2a_oauth_client_si",
      name: "N26",
      bank: "NTSBDEB1XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SVN",
    },
    /* ---------------------------------
     * ESP
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "caixabank_oauth_client_es",
      name: "CaixaBank",
      bank: "CAIXESBBXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ESP",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bbva_oauth_client_es",
      name: "BBVA",
      bank: "BBVAESMMXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ESP",
    },
    {
      valueUsage: "CanBeEntered",
      value: "santander_oauth_client_es",
      name: "Santander",
      bank: "BSCHESMMXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ESP",
    },
    {
      valueUsage: "CanBeEntered",
      value: "sabadell_oauth_client_es",
      name: "Sabadell",
      bank: "BSABESBBXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ESP",
    },
    {
      valueUsage: "CanBeEntered",
      value: "bankinter_oauth_client_es",
      name: "Bankinter",
      bank: "BKBKESMMXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "ESP",
    },

    /* ---------------------------------
     * SWE
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "swedbank_oauth_client_se",
      name: "Swedbank",
      bank: "SWEDSESSXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SWE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "handelsbanken_oauth_client_se",
      name: "Handelsbanken",
      bank: "HANDSESSXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SWE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "seb_oauth_client_se",
      name: "SEB",
      bank: "ESSESESSXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SWE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "nordea_oauth_client_se",
      name: "Nordea",
      bank: "NDEASESSXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SWE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "danske_bank_oauth_client_se",
      name: "Danske Bank",
      bank: "DABASESXXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SWE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "lansforsakringar_oauth_client_se",
      name: "Lansforsakringar Bank",
      bank: "ELLFSESSXXX",
      provider: "SaltEdge",
      isPopular: true,
      isMock: false,
      countryCode: "SWE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "sbab_client_se",
      name: "SBAB Bank AB",
      bank: "SBAVSESSXXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SWE",
    },
    {
      valueUsage: "CanBeEntered",
      value: "ica_client_se",
      name: "ICA Banken",
      bank: "IBCASES1XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SWE",
    },
    // TODO CAS-1784: no Salt Edge provider matched "Avanza Bank"
    {
      valueUsage: "CanBeEntered",
      value: "revolut_oauth_client_se",
      name: "Revolut",
      bank: "REVOLT21XXX",
      provider: "SaltEdge",
      isPopular: false,
      isMock: false,
      countryCode: "SWE",
    },
  ],
} as const satisfies EnumDefinition<PaymentProviderEnumItem>;
